import type { DesktopDatePickerProps } from "@mui/x-date-pickers/DesktopDatePicker"
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker"
import { CalendarTodayOutlined } from "@mui-symbols-material/w300"
import dayjs from "dayjs"
import type { Dayjs } from "dayjs"
import type { ReactElement } from "react"
import { forwardRef, useCallback, useMemo } from "react"

import { MaskedTextField } from "@/components/UiKit/AerosDatePicker/AerosDatePicker"
import { DATE_FORMAT } from "@/constants/formats"

export enum DateSpecialValue {
  TBD = "TBD",
  ASAP = "ASAP",
}

export type AerosDateValue = string | null | DateSpecialValue

export interface AerosCustomDatePickerProps extends Omit<DesktopDatePickerProps<Dayjs, false>, "value" | "onChange"> {
  value: AerosDateValue
  helperText?: string
  required?: boolean
  error?: boolean
  onChange: (value: AerosDateValue) => void
  "data-testid"?: string
  slots?: any
  slotProps?: any
}

export const AerosDatePickerWithCustomActions = forwardRef<HTMLDivElement, AerosCustomDatePickerProps>(
  (
    { value, helperText, required, error, format, slots, slotProps, onChange, "data-testid": testId, ...props },
    ref
  ): ReactElement => {
    const dateFormat = format || DATE_FORMAT.US
    const isSpecial = useMemo(() => value === DateSpecialValue.ASAP || value === DateSpecialValue.TBD, [value])

    const handleChange = useCallback(
      (date: Dayjs | null) => {
        onChange(date?.isValid() ? date.format(DATE_FORMAT.ISO_18601) : null)
      },
      [onChange]
    )

    return (
      <DesktopDatePicker
        {...props}
        ref={ref}
        value={!isSpecial && value ? dayjs(value) : null}
        format={dateFormat}
        onChange={handleChange}
        slots={{
          openPickerIcon: CalendarTodayOutlined,
          field: MaskedTextField,
          ...slots,
        }}
        slotProps={{
          field: {
            required,
            error: !!error,
            helperText,
            fullWidth: true,
            "data-testid": testId,
            inputProps: isSpecial ? { value: value } : undefined,
          },
          ...slotProps,
        }}
      />
    )
  }
)
